import { v4 as generateUuid } from 'uuid';
import {
  RelatedEntitlementDetails,
  RelationshipPathType,
  BillingAdmin,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CartProductSchema, ContainerId, CartProductContact } from '../../types';
import { CloudSiteTypeEnum, OperationType, ProductStatus } from '../../../types';

export const truthy = <T>(value: T): value is NonNullable<T> => !!value;

// this function is used to transform billing admins to customer contacts
export const transformBillingAdmins = (billingAdmins?: BillingAdmin[] | null | undefined): CartProductContact[] => {
  if (!billingAdmins) return [];
  return billingAdmins.map((billingAdmin) => ({
    name: billingAdmin?.fullName ?? '',
    email: billingAdmin?.email ?? '',
    accountId: billingAdmin?.atlassianAccountId ?? '',
  }));
};

export const transformEntitlementToCartProduct = ({
  entitlement,
  selectedPricingPlanId,
  containerId,
  organizationId,
  organisationName,
  operationType,
  hasAllData = true,
  includeOrgProducts = false,
  isOrgProduct = false,
  parentEntitlementId,
}: {
  entitlement: any;
  selectedPricingPlanId: string | undefined;
  containerId: ContainerId | null | undefined;
  operationType: OperationType;
  organizationId?: string | null;
  organisationName?: string | null;
  hasAllData?: boolean;
  includeOrgProducts?: boolean;
  isOrgProduct?: boolean;
  parentEntitlementId?: string;
}): CartProductSchema => ({
  id: entitlement.id ?? '',
  itemId: generateUuid(),
  name: entitlement.productName ?? '',
  cloudSiteId: entitlement.cloudSiteId ?? '',
  logoId: entitlement?.logoId ?? '',
  cloudSite: includeOrgProducts ? '' : entitlement.cloudSiteHostname ?? 'Other',
  cloudSiteType: includeOrgProducts || entitlement.cloudSiteId ? CloudSiteTypeEnum.OWN : CloudSiteTypeEnum.NEW,
  containerId,
  ...(includeOrgProducts ? { organizationId, organisationName } : {}),
  activeUsers: entitlement.activeUsers ?? 0,
  billingCycle: entitlement.billingFrequency ?? '',
  customerContacts: entitlement.endCustomerContacts
    ? entitlement.endCustomerContacts
    : entitlement.customerContacts ?? [],
  entitlementNumber: entitlement.entitlementNumber ?? '',
  listPrice: undefined,
  partnerMargin: undefined,
  otherDiscount: undefined,
  // this is the timestamp only in string format
  // only for paid plans and not for free plans, we need to add the next bill date
  nextBillDate: selectedPricingPlanId && entitlement.nextBillingTimestamp ? entitlement.nextBillingTimestamp : '',
  // this is the timestamp only in string format
  nextBillAnchorDate: entitlement.nextBillingAnchorTimestamp ?? '',
  pricingPlanId: selectedPricingPlanId ?? '',
  pricingPlanType: entitlement.pricingPlan?.pricingPlanType,
  priceEligibility: entitlement.pricingPlan?.type,
  productId: entitlement.productId,
  offeringId: entitlement.offeringId,
  offeringName: entitlement?.offering?.name,
  offeringLevel: entitlement?.offering?.level,
  operationType,
  sourceInvoiceGroupId: entitlement.billingGroupId,
  sourceTransactionAccountId: entitlement.transactionAccountId,
  productStatuses: operationType === OperationType.TRANSFER ? [ProductStatus.TRANSFER] : [],
  inTrial: entitlement.trial === true && !entitlement.isOnUpgradeTrial,
  promotions: entitlement.promotions,
  isPartnerEntitlement: entitlement.isPartnerEntitlement,
  hasAllData,
  billingAdmins: transformBillingAdmins(entitlement?.billingAdmins),
  isSandbox: entitlement?.isSandbox,
  sandboxParent: entitlement?.sandboxParent,
  isOrgProduct: !!isOrgProduct,
  /** array of linked sandbox entitlement id's to the given parent product */
  linkedSandboxIds:
    entitlement?.relatesToEntitlements
      ?.map((obj: RelatedEntitlementDetails) => {
        if (obj?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE) return obj?.entitlementId;
      })
      ?.filter(truthy) ?? [],
  isFutureDatedSubscription: entitlement.isFutureDatedSubscription,
  inactiveReason: entitlement.inactiveReason,
  isOnUpgradeTrial: !!entitlement.isOnUpgradeTrial,
  parentEntitlementId: parentEntitlementId ?? '',
});
